import { useState, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/auth'
import { DocumentUpload } from '@/components/app/DocumentUpload'

export function CaseNew() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [createdId, setCreatedId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onCreate(e: FormEvent) {
    e.preventDefault()
    if (!user) return
    if (!title.trim()) {
      setError('Give the case a short title.')
      return
    }
    setError(null)
    setBusy(true)
    const { data, error: insErr } = await supabase
      .from('cases')
      .insert({
        title: title.trim(),
        summary: summary.trim() || null,
        agent_id: user.id,
        status: 'draft',
        assigned_agent_name: null,
        assigned_agent_email: null,
      })
      .select()
      .single()
    setBusy(false)
    if (insErr || !data) {
      setError(insErr?.message ?? 'Failed to create case.')
      return
    }

    await supabase.from('case_events').insert({
      case_id: data.id,
      actor_id: user.id,
      kind: 'case.created',
      payload: {
        title: data.title,
        actor_label: user.email ?? 'Current user',
      },
    })

    setCreatedId(data.id as string)
  }

  return (
    <section className="max-w-3xl space-y-6">
      <Link
        to="/cases"
        className="inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:text-primary"
      >
        <ArrowLeft size={14} aria-hidden="true" />
        Back to cases
      </Link>

      <header>
        <h1 className="text-3xl font-bold text-primary">New case</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Give it a short title, then drop the source document. AI workflow processing kicks in
          automatically.
        </p>
      </header>

      {!createdId ? (
        <form
          onSubmit={onCreate}
          className="bg-white border border-neutral-200 rounded-lg shadow-sm p-6 space-y-5"
        >
          <div>
            <label htmlFor="title" className="block text-xs font-semibold uppercase tracking-wide text-neutral-700 mb-1">
              Case title
            </label>
            <input
              id="title"
              type="text"
              required
              autoFocus
              maxLength={140}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Sample USVI Importer LLC — April shipment"
              className="w-full h-10 px-3 rounded border border-neutral-300 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
            />
          </div>
          <div>
            <label htmlFor="summary" className="block text-xs font-semibold uppercase tracking-wide text-neutral-700 mb-1">
              Notes <span className="text-neutral-400 normal-case">(optional)</span>
            </label>
            <textarea
              id="summary"
              rows={3}
              maxLength={500}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Anything to flag for review later"
              className="w-full px-3 py-2 rounded border border-neutral-300 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded bg-primary text-white font-semibold hover:bg-primary-dark disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
          >
            {busy ? 'Creating…' : 'Create case'}
          </button>
        </form>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-lg shadow-sm p-6 space-y-5">
          <div>
            <h2 className="text-lg font-bold text-primary">Add a document</h2>
            <p className="mt-1 text-sm text-neutral-600">
              You can drop more later from the case detail page.
            </p>
          </div>
          <DocumentUpload caseId={createdId} onUploaded={() => navigate(`/cases/${createdId}`)} />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate(`/cases/${createdId}`)}
              className="text-sm font-semibold text-secondary hover:text-primary"
            >
              Skip — open case
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
